import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * Tiny inline-SVG sparkline used by the portfolio engagement cards
 * (T-0107 / C20-117).
 *
 * Why inline SVG instead of Recharts:
 *   - The shape is fixed: a single polyline over N points (≤5), no axes,
 *     no legend, no tooltip. Recharts' `<LineChart>` would emit a much
 *     heavier DOM tree with a `<ResponsiveContainer>` and the chart
 *     factory wrappers — wasteful for a 100×24 cell that's drawn 6+
 *     times on a portfolio render.
 *   - The vendor-scoring-table sparkline (apps/web/src/components/
 *     contract/vendor-scoring-table.tsx) follows the same pattern; we
 *     reuse the conventions there (4px padding, 1.5 stroke, posture-
 *     coloured endpoint dot).
 *
 * Visual contract (mirrors `prototype/ui.js → sparkline()` and the
 * `screens-portfolio.js` row sparkline cell):
 *   - 100×24 viewport (overridable via width/height)
 *   - 1.5px stroke, rounded line-joins / line-caps
 *   - colour follows the engagement posture token (handed in by the
 *     caller; v1 defaults to ink-500)
 *   - endpoint dot at the latest point in the same colour (3px radius)
 *
 * Empty / single-point handling:
 *   - 0 points  → renders a flat dashed baseline at mid-height so the
 *                 cell isn't a mystery blank space
 *   - 1 point   → renders just a dot at the centre; no line
 *   - 2+ points → polyline as expected
 *
 * Accessibility: the SVG carries `role="img"` plus an `aria-label` like
 * "Readiness trend, 5 snapshots, current 78%". Screen readers can skim
 * the portfolio without needing to land each chart individually.
 */
export interface SparklineProps {
  /**
   * Y values in chronological order (oldest first). Caller is responsible
   * for slicing the trend to the desired window (T-0107 spec: last 5
   * snapshots).
   */
  values: ReadonlyArray<number>;
  width?: number;
  height?: number;
  /** CSS var or hex. Defaults to `var(--ink-500)`. */
  stroke?: string;
  /** Optional aria-label override. */
  ariaLabel?: string;
  className?: string;
}

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 24;
const PADDING_Y = 3;

export function Sparkline({
  values,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  stroke = 'var(--ink-500)',
  ariaLabel,
  className,
}: SparklineProps) {
  const count = values.length;
  const aria =
    ariaLabel ??
    (count === 0
      ? 'Trend unavailable (no snapshots)'
      : `Readiness trend, ${count} snapshot${count === 1 ? '' : 's'}, latest ${Math.round(
          values[count - 1]!,
        )}%`);

  if (count === 0) {
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={aria}
        className={cn('block overflow-visible', className)}
      >
        <line
          x1={2}
          x2={width - 2}
          y1={height / 2}
          y2={height / 2}
          stroke="var(--ink-200)"
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const usableHeight = height - PADDING_Y * 2;

  const points = values.map((v, i) => {
    const x = count === 1 ? width / 2 : (i / (count - 1)) * width;
    const yNorm = (v - min) / range;
    const y = height - PADDING_Y - yNorm * usableHeight;
    return { x, y };
  });

  const last = points[points.length - 1]!;
  const polyline = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={aria}
      className={cn('block overflow-visible', className)}
    >
      {count >= 2 ? (
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          points={polyline}
        />
      ) : null}
      {/* Endpoint dot — emphasises the most recent value, the metric that
          drives a viewer's decision. */}
      <circle cx={last.x} cy={last.y} r={2.4} fill={stroke} />
    </svg>
  );
}

Sparkline.displayName = 'Sparkline';
